import React from 'react';
import { Button, Icon, Popover, Tooltip } from 'antd';
import moment from 'moment';
import { calculateFuelDataFromAnalogVoltage } from '../../util/methods';

function millisecondsToHms(ms) {
  const totalSeconds = Math.floor(Number(ms || 0) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  return `${hh}:${mm}:${ss}`;
}

function enrichIdleGroups(groups, fuelSensorCalibrationList) {
  return (Array.isArray(groups) ? groups : []).map((item) => {
    const durationHr = (Number(item && item.duration) || 0) / (1000 * 3600);
    const startRaw = Number(item && item.start_fuel_raw) || 0;
    const endRaw = Number(item && item.end_fuel_raw) || 0;

    const startLevelLtr = (startRaw > 0 && fuelSensorCalibrationList && fuelSensorCalibrationList.length > 0)
      ? Number(calculateFuelDataFromAnalogVoltage(startRaw, fuelSensorCalibrationList).toFixed(2))
      : 0;

    const endLevelLtr = (endRaw > 0 && fuelSensorCalibrationList && fuelSensorCalibrationList.length > 0)
      ? Number(calculateFuelDataFromAnalogVoltage(endRaw, fuelSensorCalibrationList).toFixed(2))
      : 0;

    const fuelConsumed = Number(startLevelLtr - endLevelLtr).toFixed(2);
    const fuelConsumedRateHr = durationHr > 0
      ? (Number(fuelConsumed) / Number(durationHr)).toFixed(2)
      : 0;

    return {
      ...item,
      duration_hr: durationHr.toFixed(2),
      start_fuel_level: startLevelLtr,
      end_fuel_level: endLevelLtr,
      fuel_consumed: fuelConsumed,
      fuel_consumed_rate_hr: fuelConsumedRateHr,
    };
  });
}

export function buildProcessedDataSummary(rangeData = {}, hasFuelSensorAttached = false, fuelSensorCalibrationList = []) {
  const data = rangeData && Array.isArray(rangeData.data) ? rangeData.data : [];
  const processedData = rangeData && rangeData.processedData ? rangeData.processedData : null;

  if (!processedData && data.length === 0) {
    return {
      rangeMainData: null,
      totalFuelAndDistanceConsumed: null,
      rangeFuel: 0,
      distance: 0,
      fuelWiseDistance: 0,
      avgFuel: 0,
      isFuelWiseDistance: false,
    };
  }

  const firstData = data.length > 0 ? data[0] : {};
  const lastData = data.length > 0 ? data[data.length - 1] : {};

  const firstTimestamp = Number(firstData && firstData.timestamp) || 0;
  const lastTimestamp = Number(lastData && lastData.timestamp) || 0;
  const firstKmReading = Number(firstData && firstData.odometer_kms) || 0;
  const lastKmReading = Number(lastData && lastData.odometer_kms) || 0;

  const timeElapsed = processedData && processedData.timeElapsed
    ? Number(processedData.timeElapsed)
    : (lastTimestamp - firstTimestamp);

  const totalMeterRun = processedData && processedData.distance_travelled
    ? Number(processedData.distance_travelled) / 1000
    : ((lastKmReading - firstKmReading) / 1000);

  const totalMovingTime = Number(processedData && processedData.moving_time) || 0;
  const totalMovingTimeDIN = Number(processedData && processedData.moving_time_din) || 0;

  const totalPauseTime = Number(processedData && processedData.ideal_time) || 0;
  const totalPauseTimeDIN = Number(processedData && processedData.ideal_time_din) || 0;

  const totalIgnitionTime = Number(processedData && processedData.ignition_time) || 0;
  const totalIgnitionTimeDIN = Number(processedData && processedData.ignition_time_din) || 0;

  const totalStopTime = timeElapsed - totalIgnitionTime;
  const totalStopTimeDIN = timeElapsed - totalIgnitionTimeDIN;

  const pauseTimeGroup = enrichIdleGroups(processedData && processedData.idle_time_groups, fuelSensorCalibrationList);
  const pauseTimeGroupDIN = enrichIdleGroups(processedData && processedData.idle_time_groups_din, fuelSensorCalibrationList);

  const ignitionTimeGroup = enrichIdleGroups(processedData && processedData.ignition_time_groups, fuelSensorCalibrationList);
  const ignitionTimeGroupDIN = enrichIdleGroups(processedData && processedData.ignition_time_groups_din, fuelSensorCalibrationList);

  const rangeMainData = {
    totalMovingTimeHms: millisecondsToHms(totalMovingTime),
    totalMovingTime,
    totalPauseTimeHms: millisecondsToHms(totalPauseTime),
    pauseTimeGroup,
    totalStopTimeHms: millisecondsToHms(totalStopTime > 0 ? totalStopTime : 0),
    totalIgnitionTimeHms: millisecondsToHms(totalIgnitionTime),
    totalIgnitionTime,
    totalIgnitionTimeDIN,
    total_meter_run: totalMeterRun,
    totalMovingTimeDINHms: millisecondsToHms(totalMovingTimeDIN),
    totalPauseTimeDINHms: millisecondsToHms(totalPauseTimeDIN),
    totalIgnitionTimeDINHms: millisecondsToHms(totalIgnitionTimeDIN),
    totalStopTimeDINHms: millisecondsToHms(totalStopTimeDIN > 0 ? totalStopTimeDIN : 0),
    pauseTimeGroupDIN,
    ignitionTimeGroup,
    ignitionTimeGroupDIN,
    timeElapsed,
  };

  const consumedData = processedData && processedData.newConsumed ? processedData.newConsumed : {};
  const totalFuelAndDistanceConsumed = {
    fuel: hasFuelSensorAttached
      ? Math.abs(Number(processedData && processedData.fuel_consumed_analog) || 0)
      : (Number(processedData && processedData.fuel_consumed) || 0),
    distance: Number(processedData && processedData.distance_travelled_fuel_based) || 0,
    total_refuel: Number(consumedData && consumedData.total_refuel) || 0,
    refuel_events: Array.isArray(consumedData && consumedData.refuel_events) ? consumedData.refuel_events : [],
    total_theft: Number(consumedData && consumedData.total_theft) || 0,
    theft_events: Array.isArray(consumedData && consumedData.theft_events) ? consumedData.theft_events : [],
  };

  const rangeFuel = Number(totalFuelAndDistanceConsumed.fuel) || 0;
  const distance = Number(rangeMainData.total_meter_run || 0) * 1000;
  const fuelWiseDistance = Number(totalFuelAndDistanceConsumed.distance) || 0;

  let avgFuel = 0;
  let isFuelWiseDistance = false;

  if (Math.abs(distance - fuelWiseDistance) > 1000) {
    avgFuel = rangeFuel > 0 ? ((fuelWiseDistance / 1000) / rangeFuel) : 0;
    isFuelWiseDistance = true;
  } else {
    avgFuel = rangeFuel > 0 ? ((distance / 1000) / rangeFuel) : 0;
  }

  return {
    rangeMainData,
    totalFuelAndDistanceConsumed,
    rangeFuel,
    distance,
    fuelWiseDistance,
    avgFuel,
    isFuelWiseDistance,
  };
}

function ProcessedDataSummary({
  rangeData = {},
  hasFuelSensorAttached = false,
  fuelSensorCalibrationList = [],
  summary = null,
  rangeMainData = null,
  totalFuelAndDistanceConsumed = null,
  isDarkMode = false,
  onShowIdleTime = null,
  onShowIdleTimeDin = null,
  onShowIgnitionTime = null,
  onShowIgnitionTimeDin = null,
  onShowGreenDriving = null,
  onShowOverspeed = null,
}) {
  const calculated = summary || buildProcessedDataSummary(rangeData, hasFuelSensorAttached, fuelSensorCalibrationList);
  const effectiveRangeMainData = rangeMainData || (calculated && calculated.rangeMainData) || null;
  const effectiveTotalFuelAndDistanceConsumed = totalFuelAndDistanceConsumed || (calculated && calculated.totalFuelAndDistanceConsumed) || null;
  const rangeFuel = Number((calculated && calculated.rangeFuel) || (effectiveTotalFuelAndDistanceConsumed && effectiveTotalFuelAndDistanceConsumed.fuel) || 0);
  const distance = Number((calculated && calculated.distance) || (effectiveRangeMainData && effectiveRangeMainData.total_meter_run && Number(effectiveRangeMainData.total_meter_run) * 1000) || 0);
  const fuelWiseDistance = Number((calculated && calculated.fuelWiseDistance) || (effectiveTotalFuelAndDistanceConsumed && effectiveTotalFuelAndDistanceConsumed.distance) || 0);
  const avgFuel = Number(calculated && calculated.avgFuel) || 0;
  const isFuelWiseDistance = !!(calculated && calculated.isFuelWiseDistance);

  const processedData = rangeData && rangeData.processedData ? rangeData.processedData : null;
  const greenDriving = processedData && (processedData.green_driving_summary || processedData.green_drivng_summery);
  const greenDrivingCounts = (greenDriving && greenDriving.counts) || { 1: 0, 2: 0, 3: 0 };
  const totalGreenDriving = (Number(greenDrivingCounts[1]) || 0) + (Number(greenDrivingCounts[2]) || 0) + (Number(greenDrivingCounts[3]) || 0);
  const greenDrivingHasLogs = !!(greenDriving && greenDriving.logs && (
    (greenDriving.logs[1] && greenDriving.logs[1].length > 0)
    || (greenDriving.logs[2] && greenDriving.logs[2].length > 0)
    || (greenDriving.logs[3] && greenDriving.logs[3].length > 0)
  ));

  const overspeed = processedData && processedData.overspeed_summary;
  const totalOverspeed = Number(overspeed && overspeed.total_overspeed_events) || 0;
  const hasOverspeedEvents = !!(overspeed && Array.isArray(overspeed.overspeed_events) && overspeed.overspeed_events.length > 0);
  const infoButtonStyle = {
    padding: 0,
    margin: 0,
    border: 'none',
    background: 'transparent',
    color: isDarkMode ? '#94c5ec' : '#6b7280'
  };
  const popoverClassName = isDarkMode ? 'device-details-popover device-details-popover--dark' : 'device-details-popover';

  return (
    <div>
      <div key="tatal-move" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/moving.png')} className="" />
        </div>
        <div className="gx-media-body">
          <span className="gx-mb-0 gx-text-grey gx-fs-sm">Total Moving Time</span>
          <p className="gx-mb-0">{(effectiveRangeMainData && effectiveRangeMainData.totalMovingTimeHms) || '-'}</p>
          <p className="gx-mb-0">{(effectiveRangeMainData && effectiveRangeMainData.totalMovingTimeDINHms) || '-'} <small>(DIN)</small></p>
        </div>
      </div>

      <div key="total-pause" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/pause.png')} className="" />
        </div>
        <div className="gx-media-body">
          <span className="gx-mb-0 gx-text-grey gx-fs-sm">Total Pause Time</span>
          <p className="gx-mb-0">{(effectiveRangeMainData && effectiveRangeMainData.totalPauseTimeHms) || '-'}</p>
          <p className="gx-mb-0">{(effectiveRangeMainData && effectiveRangeMainData.totalPauseTimeDINHms) || '-'} <small>(DIN)</small></p>
        </div>
        {(onShowIdleTime || onShowIdleTimeDin) && (
          <div style={{ flexDirection: 'column', display: 'flex', gap: -10 }}>
            <Tooltip title="View Idle Time Details">
              <Button
                disabled={!effectiveRangeMainData || !effectiveRangeMainData.pauseTimeGroup || effectiveRangeMainData.pauseTimeGroup.length === 0}
                shape="circle"
                icon="info-circle"
                style={infoButtonStyle}
                onClick={onShowIdleTime || undefined}
              />
            </Tooltip>
            <Tooltip title="View Idle Time Details (DIN)">
              <Button
                disabled={!effectiveRangeMainData || !effectiveRangeMainData.pauseTimeGroupDIN || effectiveRangeMainData.pauseTimeGroupDIN.length === 0}
                shape="circle"
                icon="info-circle"
                style={infoButtonStyle}
                onClick={onShowIdleTimeDin || undefined}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div key="total-ignition" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/engine-on.png')} className="" />
        </div>
        <div className="gx-media-body">
          <span className="gx-mb-0 gx-text-grey gx-fs-sm">Total Ignition Time</span>
          <p className="gx-mb-0">{(effectiveRangeMainData && effectiveRangeMainData.totalIgnitionTimeHms) || '-'}</p>
          <p className="gx-mb-0">{(effectiveRangeMainData && effectiveRangeMainData.totalIgnitionTimeDINHms) || '-'} <small>(DIN)</small></p>
        </div>
        {(onShowIgnitionTime || onShowIgnitionTimeDin) && (
          <div style={{ flexDirection: 'column', display: 'flex', gap: -10 }}>
            {onShowIgnitionTime && (
              <Tooltip title="View Ignition Time Details">
                <Button
                  disabled={!effectiveRangeMainData || !effectiveRangeMainData.ignitionTimeGroup || effectiveRangeMainData.ignitionTimeGroup.length === 0}
                  shape="circle"
                  icon="info-circle"
                  style={infoButtonStyle}
                  onClick={onShowIgnitionTime || undefined}
                />
              </Tooltip>
            )}
            {onShowIgnitionTimeDin && effectiveRangeMainData && effectiveRangeMainData.ignitionTimeGroupDIN && effectiveRangeMainData.ignitionTimeGroupDIN.length > 0 && (
              <Tooltip title="View Ignition Time Details (DIN)">
                <Button
                  shape="circle"
                  icon="info-circle"
                  style={infoButtonStyle}
                  onClick={onShowIgnitionTimeDin || undefined}
                />
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <div key="total-stop" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/off.png')} className="" />
        </div>
        <div className="gx-media-body">
          <span className="gx-mb-0 gx-text-grey gx-fs-sm">Total Stop Time</span>
          <p className="gx-mb-0">{(effectiveRangeMainData && effectiveRangeMainData.totalStopTimeHms) || '-'}</p>
          <p className="gx-mb-0">{(effectiveRangeMainData && effectiveRangeMainData.totalStopTimeDINHms) || '-'} <small>(DIN)</small></p>
        </div>
      </div>

      <div key="harsh-driving" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <Icon type="warning" style={{ fontSize: 20, color: totalGreenDriving > 0 ? '#faad14' : (isDarkMode ? '#9fb3c8' : undefined) }} />
        </div>
        <div className="gx-media-body">
          <span className="gx-mb-0 gx-text-grey gx-fs-sm">Green Driving</span>
          <p className="gx-mb-0">{totalGreenDriving}</p>
        </div>
        {onShowGreenDriving && (
          <div>
            <Tooltip title="View Green Driving events">
              <Button
                disabled={!greenDrivingHasLogs}
                shape="circle"
                icon="info-circle"
                style={infoButtonStyle}
                onClick={onShowGreenDriving}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div key="overspeeding" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <Icon type="dashboard" style={{ fontSize: 20, color: totalOverspeed > 0 ? '#faad14' : (isDarkMode ? '#9fb3c8' : undefined) }} />
        </div>
        <div className="gx-media-body">
          <span className="gx-mb-0 gx-text-grey gx-fs-sm">Overspeeding</span>
          <p className="gx-mb-0">{totalOverspeed}</p>
        </div>
        {onShowOverspeed && (
          <div>
            <Tooltip title="View Overspeed events">
              <Button
                disabled={!hasOverspeedEvents}
                shape="circle"
                icon="info-circle"
                style={infoButtonStyle}
                onClick={onShowOverspeed}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div key="run" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/counter.png')} className="" />
        </div>
        <div className="gx-media-body">
          <span className="gx-mb-0 gx-text-grey gx-fs-sm">Distance travelled</span>
          <p className="gx-mb-0">{distance ? `${Number(distance / 1000).toFixed(2)} Kms` : '-'}</p>
        </div>
      </div>

      <div key="fuel-total" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/fuel.png')} className="" />
        </div>
        <div className="gx-media-body">
          <div className="ant-row-flex gx-justify-content-between">
            <div className="ant-row-flex-start">
              <span className="gx-mb-0 gx-text-grey gx-fs-sm">
                Fuel Consumed {isFuelWiseDistance && fuelWiseDistance ? `(${(fuelWiseDistance / 1000).toFixed(2)} Kms)` : ''}
              </span>
              <p className="gx-mb-0">{rangeFuel ? `${Number(rangeFuel).toFixed(2)} ltr(s)` : '-'}</p>
            </div>
          </div>
        </div>
      </div>

      <div key="refuel-total" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/refuel.png')} className="" style={{ width: 24, height: 24 }} />
        </div>
        <div className="gx-media-body">
          <div className="ant-row-flex gx-justify-content-between">
            <div className="ant-row-flex-start">
              <span className="gx-mb-0 gx-text-grey gx-fs-sm">Possible Refuel</span>
              <p className="gx-mb-0">{`${Number((effectiveTotalFuelAndDistanceConsumed && effectiveTotalFuelAndDistanceConsumed.total_refuel) || 0).toFixed(2)} ltr(s)`}</p>
            </div>
            <Popover
              overlayClassName={popoverClassName}
              content={
                <div>
                  {((effectiveTotalFuelAndDistanceConsumed && effectiveTotalFuelAndDistanceConsumed.refuel_events) || []).map((refuel, index) => (
                    <div key={index}>
                      {Number(refuel && refuel.refuelAmount || 0).toFixed(2)} ltr. <small>{refuel && refuel.timestamp ? moment(refuel.timestamp).toLocaleString() : '-'}</small>
                    </div>
                  ))}
                </div>
              }
              title="Refuel Events"
            >
              <Button shape="circle" icon="info-circle" style={infoButtonStyle} />
            </Popover>
          </div>
        </div>
      </div>

      <div key="theft-total" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/theft.png')} className="" style={{ width: 24, height: 24 }} />
        </div>
        <div className="gx-media-body">
          <div className="ant-row-flex gx-justify-content-between">
            <div className="ant-row-flex-start">
              <span className="gx-mb-0 gx-text-grey gx-fs-sm">Possible Drain</span>
              <p className="gx-mb-0">{`${Number((effectiveTotalFuelAndDistanceConsumed && effectiveTotalFuelAndDistanceConsumed.total_theft) || 0).toFixed(2)} ltr(s)`}</p>
            </div>
            <Popover
              overlayClassName={popoverClassName}
              content={
                <div>
                  {((effectiveTotalFuelAndDistanceConsumed && effectiveTotalFuelAndDistanceConsumed.theft_events) || []).map((theft, index) => (
                    <div key={index}>
                      {Number(theft && theft.theftAmount || 0).toFixed(2)} ltr. <small>{theft && theft.timestamp ? moment(theft.timestamp).toLocaleString() : '-'}</small>
                    </div>
                  ))}
                </div>
              }
              title="Drain Events"
            >
              <Button shape="circle" icon="info-circle" style={infoButtonStyle} />
            </Popover>
          </div>
        </div>
      </div>

      <div key="avg" className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list">
        <div className="gx-mr-3">
          <img alt="..." src={require('assets/images/icon/fuel-car.png')} className="" />
        </div>
        <div className="gx-media-body">
          <span className="gx-mb-0 gx-text-grey gx-fs-sm">Avg. Fuel Consumed</span>
          <p className="gx-mb-0">
            {hasFuelSensorAttached
              ? (rangeFuel > 0 ? `${Number(distance / (1000 * rangeFuel)).toFixed(2)} Km/ltr.` : '-')
              : (avgFuel > 0 ? `${Number(avgFuel).toFixed(2)} Km/ltr` : '-')}
          </p>
          <p className="gx-mb-0">
            {hasFuelSensorAttached && effectiveRangeMainData && effectiveRangeMainData.totalIgnitionTime
              ? `${Number(rangeFuel / (effectiveRangeMainData.totalIgnitionTime / 3600000)).toFixed(2)} ltr./hr.`
              : ''}
            {hasFuelSensorAttached && effectiveRangeMainData && effectiveRangeMainData.totalIgnitionTimeDIN
              ? ` (${Number(rangeFuel / (effectiveRangeMainData.totalIgnitionTimeDIN / 3600000)).toFixed(2)} ltr./hr. (DIN))`
              : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

export default ProcessedDataSummary;
